import {
    VORANK_BILD_MAX_ZEICHEN,
    VORANK_BILD_PRAEFIX,
    VORANK_DATUM_MUSTER,
    VORANK_FUNKTION_MAX,
    VORANK_KORREKTUR_MAX,
    VORANK_NAME_MAX,
    VORANK_NAME_MIN,
    VORANK_TOKEN_MUSTER,
} from './vorankUnterschriftTypes.js';
import type { VorankRouteAnfrage } from './vorankUnterschriftTypes.js';

/**
 * Vorabprüfung einer Anfrage an `api/vorank-unterschrift.ts` — rein, ohne
 * Supabase und ohne Browser-APIs (docs/vorankuendigung-unterschrift-plan.md
 * §4.3, §6).
 *
 * Zwei Aufrufer mit denselben Grenzen: die öffentliche Seite (sofortige,
 * verständliche Absage vor dem Absenden) und die Vercel-Route (der Riegel vor
 * der Service-Role-RPC). Die Grenzen stehen im Vertrag
 * (`vorankUnterschriftTypes.ts`) und gleichen denen der RPC.
 *
 * ═══ IMPORTE MIT `.js` ═══════════════════════════════════════════════════
 * Die Route läuft als ESM auf Vercel; dort bricht jeder Wert-Import ohne
 * Endung (Lehre `materialMail.ts`). Deshalb hier `./vorankUnterschriftTypes.js`
 * — Vite und tsc lösen das auf die `.ts`-Datei auf (Muster
 * `sifa/zertifikatPdf.ts`). Diese Datei darf NICHTS importieren, was
 * `supabaseClient`, `import.meta.env` oder `window` anfasst.
 */

type Zeile = Record<string, unknown>;

/** Steuerzeichen außer Tab und Zeilenumbruch — die haben in einem Namen (Mail-Betreff!) nichts verloren. */
const STEUERZEICHEN = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;
/** Im Namen und in der Funktion zusätzlich kein Umbruch und kein Tab. */
const ZEILENUMBRUCH = /[\t\n\r]/;
/** Nach dem Präfix nur Base64. */
const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;

/** Der Token aus dem Link: genau 64 Hex-Zeichen, klein. */
export const istVorankToken = (x: unknown): x is string =>
    typeof x === 'string' && VORANK_TOKEN_MUSTER.test(x);

/**
 * Ist das ein Unterschriftsbild, wie es die RPC annimmt? PNG als data-URL
 * mit Base64-Nutzlast, höchstens `VORANK_BILD_MAX_ZEICHEN` Zeichen insgesamt.
 * Auch beim LESEN benutzt: ein Bild, das hier durchfällt, landet nie in
 * einem `<img src>` oder im PDF.
 */
export const istUnterschriftsBild = (x: unknown): x is string =>
    typeof x === 'string'
    && x.length <= VORANK_BILD_MAX_ZEICHEN
    && x.startsWith(VORANK_BILD_PRAEFIX)
    && BASE64.test(x.slice(VORANK_BILD_PRAEFIX.length));

/**
 * Ein Kalendertag `YYYY-MM-DD`, den es gibt (kein 31.02.) — so liefert ihn
 * `<input type="date">`, so nimmt ihn die RPC als `date` an (E6).
 */
export const istKalenderTag = (x: unknown): x is string => {
    if (typeof x !== 'string' || !VORANK_DATUM_MUSTER.test(x)) return false;
    const [jahr, monat, tag] = x.split('-').map(Number);
    const d = new Date(Date.UTC(jahr, monat - 1, tag));
    return d.getUTCFullYear() === jahr && d.getUTCMonth() === monat - 1 && d.getUTCDate() === tag;
};

const BERLIN_ISO_TAG = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
});

/**
 * Zeitpunkt → Berliner Kalendertag `YYYY-MM-DD` — die Zone der Baustelle,
 * nie die des Geräts oder des Servers (Vercel: UTC). Unlesbar → ''.
 */
export const berlinIsoTag = (zeitpunkt: Date | number | string | null | undefined): string => {
    if (zeitpunkt === null || zeitpunkt === undefined || zeitpunkt === '') return '';
    const ms = zeitpunkt instanceof Date ? zeitpunkt.getTime() : typeof zeitpunkt === 'number' ? zeitpunkt : Date.parse(zeitpunkt);
    if (!Number.isFinite(ms)) return '';
    const teile: Record<string, string> = {};
    for (const t of BERLIN_ISO_TAG.formatToParts(ms)) teile[t.type] = t.value;
    return `${teile.year}-${teile.month}-${teile.day}`;
};

/** `YYYY-MM-DD` → „TT.MM.JJJJ“; kein gültiger Kalendertag → ''. */
export const tagAusIso = (iso: string | null | undefined): string => {
    if (!istKalenderTag(iso)) return '';
    const [jahr, monat, tag] = iso.split('-');
    return `${tag}.${monat}.${jahr}`;
};

/**
 * Großzügig: die Route kennt den Tag der Fassung nicht. Genau prüft die RPC
 * (heute in Berlin, nicht vor dem Tag der Fassung) — hier fällt nur ab, was
 * sicher nicht stimmt.
 */
const DATUM_SPIELRAUM_MS = 24 * 60 * 60 * 1000;

const pruefeDatum = (roh: unknown, opt: AnfragePruefungOptionen): string | null => {
    if (roh === undefined || roh === null || roh === '') return 'Bitte das Datum der Unterschrift eintragen.';
    if (!istKalenderTag(roh)) return 'Das Datum der Unterschrift ist ungültig.';
    if (opt.zukunftPruefen && roh > berlinIsoTag((opt.jetzt ?? new Date()).getTime() + DATUM_SPIELRAUM_MS)) {
        return 'Das Datum der Unterschrift darf nicht in der Zukunft liegen.';
    }
    return null;
};

export interface AnfragePruefungOptionen {
    /**
     * Ein Datum sicher in der Zukunft grob abweisen — NUR die Route (Uhr des
     * Servers). Im Browser aus: geht die Uhr des Geräts nach, erlaubt die
     * Seite den Tag der Fassung als Obergrenze (`datumGrenzen`), und eine
     * Prüfung gegen die Geräte-Uhr widerspräche dem.
     */
    zukunftPruefen?: boolean;
    /** Nur für Tests: der Zeitpunkt, gegen den `zukunftPruefen` rechnet. */
    jetzt?: Date;
}

const pruefeName = (roh: unknown): string | null => {
    if (typeof roh !== 'string' || roh.trim().length < VORANK_NAME_MIN) {
        return `Bitte den Namen eintragen (mindestens ${VORANK_NAME_MIN} Zeichen).`;
    }
    if (roh.trim().length > VORANK_NAME_MAX) return `Der Name ist zu lang (höchstens ${VORANK_NAME_MAX} Zeichen).`;
    if (STEUERZEICHEN.test(roh) || ZEILENUMBRUCH.test(roh)) return 'Der Name enthält unzulässige Zeichen.';
    return null;
};

const pruefeFunktion = (roh: unknown): string | null => {
    // Die Funktion ist freiwillig: fehlt sie, ist das kein Fehler.
    if (roh === undefined || roh === null) return null;
    if (typeof roh !== 'string') return 'Die Funktion ist unlesbar.';
    if (roh.trim().length > VORANK_FUNKTION_MAX) return `Die Funktion ist zu lang (höchstens ${VORANK_FUNKTION_MAX} Zeichen).`;
    if (STEUERZEICHEN.test(roh) || ZEILENUMBRUCH.test(roh)) return 'Die Funktion enthält unzulässige Zeichen.';
    return null;
};

const pruefeBild = (roh: unknown): string | null => {
    if (typeof roh !== 'string' || roh.length <= VORANK_BILD_PRAEFIX.length) return 'Bitte zuerst unterschreiben.';
    if (!roh.startsWith(VORANK_BILD_PRAEFIX)) return 'Die Unterschrift muss als PNG-Bild vorliegen.';
    if (roh.length > VORANK_BILD_MAX_ZEICHEN) {
        return 'Die Unterschrift ist zu groß — bitte löschen und erneut unterschreiben.';
    }
    if (!istUnterschriftsBild(roh)) return 'Das Bild der Unterschrift ist beschädigt — bitte erneut unterschreiben.';
    return null;
};

const pruefeKorrektur = (roh: unknown): string | null => {
    if (typeof roh !== 'string' || !roh.trim()) return 'Bitte beschreiben Sie, was korrigiert werden soll.';
    if (roh.trim().length > VORANK_KORREKTUR_MAX) return `Der Text ist zu lang (höchstens ${VORANK_KORREKTUR_MAX} Zeichen).`;
    if (STEUERZEICHEN.test(roh)) return 'Der Text enthält unzulässige Zeichen.';
    return null;
};

/**
 * Prüft eine Anfrage VOR dem Absenden bzw. vor der RPC. Rückgabe: deutscher
 * Klartext für den Bauherrn — oder `null`, wenn alles passt.
 *
 * Die Längen gelten für den getrimmten Wert; wer absendet, schickt ihn
 * getrimmt. Das Argument ist als `VorankRouteAnfrage` typisiert, wird aber
 * wie fremdes JSON gelesen: die Route reicht hier den rohen Body herein.
 * Das Datum prüft sie auf Form und Kalendertag; „nicht in der Zukunft“ nur
 * mit `opt.zukunftPruefen` (die Route), nie im Browser.
 */
export const pruefeAnfrage = (anfrage: VorankRouteAnfrage, opt: AnfragePruefungOptionen = {}): string | null => {
    const a: Zeile | null = anfrage !== null && typeof anfrage === 'object' && !Array.isArray(anfrage)
        ? (anfrage as unknown as Zeile)
        : null;
    if (!a) return 'Die Anfrage ist unvollständig.';
    if (a.aktion !== 'unterschreiben' && a.aktion !== 'korrektur') return 'Unbekannte Aktion.';
    if (!istVorankToken(a.token)) return 'Der Link ist ungültig.';
    const name = pruefeName(a.name);
    if (name) return name;
    if (a.aktion === 'korrektur') return pruefeKorrektur(a.text);
    const funktion = pruefeFunktion(a.funktion);
    if (funktion) return funktion;
    const datum = pruefeDatum(a.datum, opt);
    if (datum) return datum;
    if (a.art !== 'gezeichnet' && a.art !== 'bild') return 'Unbekannte Art der Unterschrift.';
    return pruefeBild(a.bild);
};
